/**
 * CameraService.js - Utility functions for camera calculations
 * Pure functions for POI position calculations and camera math
 */

import * as THREE from 'three/webgpu'
import { MOBILE_PORTRAIT_POI_TARGETS } from './CameraConfig.js'

/**
 * Get camera position by POI name from scene groups
 * @param {string} poiName - POI name (e.g., 'POI_HA1', 'POI_LP1', 'POI_SAT')
 * @param {Object} scenesRef - Reference to scenes
 * @param {boolean} isMobilePortrait - True when on mobile portrait (≤767px, portrait orientation)
 * @returns {Object|null} {position: Vector3, target: Vector3} or null
 */
export function getSaxavordCameraPosition(poiName, scenesRef, isMobilePortrait = false) {
  if (!scenesRef.scene1?.current) return null
  
  // Map POI names to CAM/TAR names in the model
  const mapping = {
    'POI_HA1': { cam: 'CAM_HA', tar: 'TAR_HA' },
    'POI_LP1': { cam: 'CAM_LP', tar: 'TAR_LP' },
    'POI_SAT': { cam: 'CAM_SAT', tar: 'TAR_SA' },
    'POI_MET': { cam: 'CAM_MET', tar: 'TAR_MET' },
    'POI_RAF': { cam: 'CAM_RAF', tar: 'TAR_RAF' },
    'POI_HIS': { cam: 'CAM_HIS', tar: 'TAR_HIS' }
  }
  
  const config = mapping[poiName]
  if (!config) return null
  
  let camPosition = null
  let tarPosition = null
  
  // Find CAM and TAR groups in scene
  scenesRef.scene1.current.traverse((object) => {
    if (object.name === config.cam) {
      camPosition = object.position.clone()
    }
    if (object.name === config.tar) {
      tarPosition = object.position.clone()
    }
  })
  
  if (!camPosition || !tarPosition) return null

  // Apply mobile portrait look-at override if available
  if (isMobilePortrait && MOBILE_PORTRAIT_POI_TARGETS[poiName]) {
    tarPosition = MOBILE_PORTRAIT_POI_TARGETS[poiName].clone()
  }

  return {
    position: camPosition,
    target: tarPosition
  }
}

// SCENE HOTSPOT CALCULATIONS
// ============================================================================

/**
 * Project 3D world position to 2D screen coordinates
 * @param {THREE.Vector3} worldPosition - Position in world space
 * @param {THREE.Camera} camera - Camera to project from
 * @param {Object} size - Viewport size {width, height}
 * @param {Object} options - Optional settings
 * @param {boolean} options.clampToScreen - If true, clamps coordinates to stay on screen
 * @param {number} options.padding - Padding from screen edges when clamping (default: 50px)
 * @returns {Object} {x, y, isVisible, z, isClamped} screen coordinates
 */
export function projectPositionToScreen(worldPosition, camera, size, options = {}) {
  const { clampToScreen = false, padding = 50 } = options
  const screenPosition = worldPosition.clone().project(camera)

  // Convert to pixel coordinates
  let x = (screenPosition.x * 0.5 + 0.5) * size.width
  let y = (screenPosition.y * -0.5 + 0.5) * size.height

  // Check if behind camera and within viewport bounds (with margin for dimension lines)
  const margin = 200 // Allow some margin for dimension lines that extend beyond POI position
  const isBehindCamera = screenPosition.z >= 1
  const isOnScreen = x >= -margin && 
                     x <= size.width + margin && 
                     y >= -margin && 
                     y <= size.height + margin
  const isVisible = !isBehindCamera && isOnScreen

  // Clamp to screen edges if requested and POI is off-screen or behind camera
  let isClamped = false
  if (clampToScreen && (isBehindCamera || !isOnScreen)) {
    // Clamp x to stay within padding bounds
    x = Math.max(padding, Math.min(size.width - padding, x))
    // Clamp y to stay within padding bounds
    y = Math.max(padding, Math.min(size.height - padding, y))
    isClamped = true
  }

  return {
    x,
    y,
    isVisible: clampToScreen ? true : isVisible, // Always visible when clamped
    z: screenPosition.z,
    isClamped,
    isBehindCamera
  }
}

/**
 * Get all hotspots (POIs) for a scene
 * @param {Object} scenesRef - Scene references
 * @param {number} hdriBlend - Scene transition value (0 = scene1, 1 = scene2)
 * @param {THREE.Camera} camera - Camera for projection
 * @param {Object} size - Viewport size
 * @returns {Array} Array of hotspot objects
 */
export function getSceneHotspots(scenesRef, hdriBlend, camera, size) {
  if (!scenesRef || !camera || !size) return []

  const poiGroups = []
  const tarGroups = []
  const hotspots = []

  // Show POIs from saxhq model (single scene)
  if (scenesRef.scene1?.current) {
    scenesRef.scene1.current.traverse((object) => {
      if (object.name?.startsWith('POI_')) {
        poiGroups.push(object)
      }
    })
  }

  if (poiGroups.length === 0) return []

  // Convert POI positions to screen coordinates with clamping to keep labels on screen
  poiGroups.forEach((group) => {
    const worldPosition = new THREE.Vector3()
    group.getWorldPosition(worldPosition)

    // POI_MET is behind the camera on mobile portrait - disable clamping so it
    // simply becomes invisible rather than snapping to the screen edge
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 767
    const shouldClamp = !(isMobile && group.name === 'POI_MET')

    const screenInfo = projectPositionToScreen(worldPosition, camera, size, { 
      clampToScreen: shouldClamp, 
      padding: 80 // Keep labels 80px from screen edges
    })

    hotspots.push({
      name: group.name,
      x: screenInfo.x,
      y: screenInfo.y,
      isVisible: screenInfo.isVisible,
      isClamped: screenInfo.isClamped,
      isBehindCamera: screenInfo.isBehindCamera
    })
  })
  
  return hotspots
}

// ============================================================================
// CAMERA ANIMATION HELPERS
// ============================================================================

/**
 * Smooth easing function (ease-in-out cubic)
 * @param {number} progress - Value from 0 to 1
 * @returns {number} Eased value
 */
export function easeInOutCubic(progress) {
  return progress < 0.5
    ? 4 * progress * progress * progress
    : 1 - Math.pow(-2 * progress + 2, 3) / 2
}
